import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCourier,
  getPartnerLinkResolved,
  refreshIsPartnerLinked,
} from '../state/courierSlice';
import { getIsAuthenticated } from '../state/authSlice';
import { AppDispatch } from '../store';

/**
 * Resolves the partner-link answer for a session that signed in after the
 * courier bootstrap had already run.
 *
 * `/PartnerLinks/is-partner-linked` is `$authenticated ALLOW`, and fetchCourier
 * runs before anyone has signed in — so on a fresh browser the bootstrap skips
 * the probe and leaves `partnerLinkResolved: false`. Login is a client-side
 * navigate(), so App never remounts and fetchCourier never runs again. Without
 * this the flag would stay false all session and AdminProtectedRoute would
 * hide Warehouse Management from a courier whose warehouse is another OX
 * tenant.
 *
 * Does nothing when the bootstrap already got an answer (the normal
 * already-signed-in page load), so this never adds a second request.
 */
export const usePartnerLinkRefresh = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const courier = useSelector(getCourier);
  const partnerLinkResolved = useSelector(getPartnerLinkResolved);
  const courierId = courier?.id;

  // The thunk's own fulfilled case flips partnerLinkResolved, but the request
  // is in flight for a beat before that — without this guard a re-render in
  // that window would fire a second one.
  const requestedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !courierId || partnerLinkResolved) return;

    const key = String(courierId);
    if (requestedFor.current === key) return;
    requestedFor.current = key;

    dispatch(refreshIsPartnerLinked(key));
  }, [isAuthenticated, courierId, partnerLinkResolved, dispatch]);
};
